import { useEffect, useMemo, useState } from 'react';
import { Dialog, TextField } from '@mui/material';

import { parseCustomerIds } from '@features/incidents/helpers';
import { useAttachIncidentCustomersMutation } from '@features/incidents/mutations';

import s from './AttachCustomersDialog.module.css';

const MAX_IDS_PER_REQUEST = 5000;
const MAX_INVALID_SHOWN = 5;

type AttachCustomersDialogProps = {
  open: boolean;
  onClose: () => void;
  incidentId: string;
};

function AttachCustomersDialog({ open, onClose, incidentId }: AttachCustomersDialogProps) {
  const [idsText, setIdsText] = useState('');
  const [note, setNote] = useState('');
  const attachCustomers = useAttachIncidentCustomersMutation();

  useEffect(() => {
    if (!open) return;
    setIdsText('');
    setNote('');
  }, [open]);

  const parsed = useMemo(() => parseCustomerIds(idsText), [idsText]);
  const tooMany = parsed.valid.length > MAX_IDS_PER_REQUEST;
  const canSubmit =
    parsed.valid.length > 0 &&
    parsed.invalid.length === 0 &&
    !tooMany &&
    !attachCustomers.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    attachCustomers.mutate(
      { incidentId, customerIds: parsed.valid, note: note.trim() || undefined },
      { onSuccess: () => onClose() },
    );
  };

  const invalidPreview = parsed.invalid.slice(0, MAX_INVALID_SHOWN).join(', ');
  const invalidMore = parsed.invalid.length - MAX_INVALID_SHOWN;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '14px',
            width: 560,
            maxWidth: '92vw',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)',
          },
        },
      }}>
      <form
        className={s.body}
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}>
        <h3 className={s.title}>Attach customers</h3>
        <p className={s.subtitle}>
          Paste customer IDs (one per line or comma-separated). Customers already attached are
          skipped, so re-pasting a list is safe.
        </p>

        <div className={s.field}>
          <span className={s.fieldLabel}>Customer IDs</span>
          <TextField
            value={idsText}
            onChange={(event) => setIdsText(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={6}
            maxRows={14}
            autoFocus
            placeholder={'6f1c1a2e-…\n9b7d3c4f-…'}
            slotProps={{ input: { className: s.mono } }}
          />
          <div className={s.hintRow}>
            <span className={s.hint}>
              {parsed.valid.length.toLocaleString()} customer ID
              {parsed.valid.length === 1 ? '' : 's'}
            </span>
            {parsed.invalid.length > 0 && (
              <span className={s.error}>
                Not a customer ID: {invalidPreview}
                {invalidMore > 0 ? ` and ${invalidMore} more` : ''}
              </span>
            )}
            {tooMany && (
              <span className={s.error}>
                At most {MAX_IDS_PER_REQUEST.toLocaleString()} IDs per request.
              </span>
            )}
          </div>
        </div>

        <div className={s.field}>
          <span className={s.fieldLabel}>Note (optional)</span>
          <TextField
            value={note}
            onChange={(event) => setNote(event.target.value)}
            size="small"
            fullWidth
            placeholder="e.g. Duplicate charge refunded 2026-09-01"
          />
        </div>

        <div className={s.actions}>
          <button
            type="button"
            className={s.cancelBtn}
            onClick={onClose}
            disabled={attachCustomers.isPending}>
            Cancel
          </button>
          <button type="submit" className={s.saveBtn} disabled={!canSubmit}>
            {attachCustomers.isPending
              ? 'Attaching…'
              : `Attach ${parsed.valid.length > 0 ? parsed.valid.length.toLocaleString() : ''}`.trim()}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export default AttachCustomersDialog;
